import { logger } from "@/utils/logger";

import { AIResponse } from './aiResponseParser';
import { getSessionStartTime } from './sessionBoundary';
import { hasVehicleContext } from './vehicleContextHelpers';

export const handleFormLogic = (
  parsedResponse: AIResponse,
  activeForm: any,
  setActiveForm: (form: any, aiData?: { [key: string]: any }) => void,
  activateOfferForm?: () => void,
  closeForm?: (force?: boolean) => void,  // Update closeForm parameter
  setLanguage?: (lang: 'fr' | 'en') => void,
  wasFormJustClosed: boolean = false,
  setIsExpanded?: (expanded: boolean) => void,
  messageTimestamp?: Date | number // Add timestamp to prevent actions from restored messages
): void => {
  // CRITICAL: Test if this function is even being called
  logger.log('🚨🚨🚨 HANDLEFORMLOGIC FUNCTION CALLED! 🚨🚨🚨');
  
  // Check if a form was submitted very recently (10 seconds) to prevent double-submission of same form
  // This is a SHORT window to prevent duplicate form bugs, not to block legitimate new form requests
  const lastFormSubmissionTime = localStorage.getItem('lastFormSubmissionTime');
  const lastSubmittedFormType = localStorage.getItem('lastSubmittedFormType');
  const formSubmissionCooldown = 10 * 1000; // 10 seconds (reduced from 2 minutes)
  const now = Date.now();
  
  // Only block if SAME form type is trying to open within cooldown period
  const extractedFormType = typeof parsedResponse['Form'] === 'object' 
    ? parsedResponse['Form']?.Type 
    : parsedResponse['Form'];
  
  if (lastFormSubmissionTime && 
      lastSubmittedFormType && 
      extractedFormType === lastSubmittedFormType &&
      (now - parseInt(lastFormSubmissionTime)) < formSubmissionCooldown) {
    const timeRemaining = Math.ceil((formSubmissionCooldown - (now - parseInt(lastFormSubmissionTime))) / 1000);
    logger.log('🚨 Same form type was recently submitted, ignoring for', timeRemaining, 'more seconds');
    logger.log('🔥 Form submission cooldown active for same form type - ignoring');
    
    // Still process language switching
    if (setLanguage) {
      let detectedLanguage = null;
      
      if (parsedResponse['Language'] && typeof parsedResponse['Language'] === 'object' && parsedResponse['Language']['Current']) {
        detectedLanguage = parsedResponse['Language']['Current'];
      } else if (parsedResponse['Language'] && typeof parsedResponse['Language'] === 'string') {
        detectedLanguage = parsedResponse['Language'];
      }
      
      if (detectedLanguage) {
        const lang = detectedLanguage.toLowerCase();
        let languageCode = null;
        
        if (lang === 'french' || lang === 'français' || lang === 'fr') {
          languageCode = 'fr';
        } else if (lang === 'english' || lang === 'anglais' || lang === 'en') {
          languageCode = 'en';
        }
        
        if (languageCode) {
          setLanguage(languageCode);
        }
      }
    }
    
    return; // Exit early, don't process form logic
  }
  logger.log('🔥 handleFormLogic - Checking for Form field...');
  
  // 🔥 NEW: Handle Language switching
  logger.log('🌐🌐🌐 LANGUAGE SWITCHING - setLanguage function available:', !!setLanguage);
  
  if (setLanguage) {
    let detectedLanguage = null;
    
    // Check for Language.Current (new format)
    if (parsedResponse['Language'] && typeof parsedResponse['Language'] === 'object' && parsedResponse['Language']['Current']) {
      detectedLanguage = parsedResponse['Language']['Current'];
      logger.log('🌐🌐🌐 Language.Current detected in response:', detectedLanguage);
      logger.log('🔥 Language.Current detected in response:', detectedLanguage);
    }
    // Check for direct Language field (legacy format)
    else if (parsedResponse['Language'] && typeof parsedResponse['Language'] === 'string') {
      detectedLanguage = parsedResponse['Language'];
      logger.log('🌐🌐🌐 Language detected in response:', detectedLanguage);
      logger.log('🔥 Language detected in response:', detectedLanguage);
    }
    
    logger.log('🌐🌐🌐 LANGUAGE SWITCHING - detectedLanguage:', detectedLanguage);
    
    // Map French/English words to language codes
    if (detectedLanguage) {
      let languageCode = null;
      const lang = detectedLanguage.toLowerCase();
      
      if (lang === 'french' || lang === 'français' || lang === 'fr') {
        languageCode = 'fr';
      } else if (lang === 'english' || lang === 'anglais' || lang === 'en') {
        languageCode = 'en';
      }
      
      logger.log('🌐🌐🌐 LANGUAGE SWITCHING - Mapped languageCode:', languageCode);
      
      if (languageCode) {
        logger.log('🌐🌐🌐 ABOUT TO SWITCH LANGUAGE TO:', languageCode);
        logger.log('🔥 Switching language to:', languageCode);
        setLanguage(languageCode);
        logger.log('🌐🌐🌐 LANGUAGE SWITCH COMPLETED!');
      } else {
        logger.log('🌐🌐🌐 Unrecognized language value:', detectedLanguage);
        logger.log('🔥 Unrecognized language value:', detectedLanguage);
      }
    } else {
      logger.log('🌐🌐🌐 No language detected in response');
    }
  } else {
    logger.log('🌐🌐🌐 setLanguage function not provided');
  }
  
  // 🔥 NEW: Check for CARFAX_URL in vehicles AND at top level and force Carfax form
  const vehiclesCards = parsedResponse['Reply_Message']?.Content?.Inventory;
  const topLevelCarfaxUrl = parsedResponse['CARFAX_URL'];
  
  logger.log('🔥 CARFAX CHECK - Content.Inventory:', JSON.stringify(vehiclesCards, null, 2));
  logger.log('🔥 CARFAX CHECK - Top level CARFAX_URL:', topLevelCarfaxUrl);
  
  const hasVehiclesWithCarfax = vehiclesCards && 
                               Array.isArray(vehiclesCards) && 
                               vehiclesCards.length > 0 &&
                               vehiclesCards.some(vehicle => {
                                 logger.log('🔥 CARFAX CHECK - Vehicle keys:', Object.keys(vehicle));
                                 logger.log('🔥 CARFAX CHECK - Vehicle CARFAX_URL:', vehicle.CARFAX_URL);
                                 return vehicle.CARFAX_URL;
                               });
  
  const hasTopLevelCarfax = !!topLevelCarfaxUrl;
  const shouldForceCarfax = hasVehiclesWithCarfax || hasTopLevelCarfax;
  
  logger.log('🔥 CARFAX CHECK - hasVehiclesWithCarfax result:', hasVehiclesWithCarfax);
  logger.log('🔥 CARFAX CHECK - hasTopLevelCarfax result:', hasTopLevelCarfax);
  logger.log('🔥 CARFAX CHECK - shouldForceCarfax result:', shouldForceCarfax);
  
  if (shouldForceCarfax) {
    logger.log('🔥 CARFAX_URL detected, forcing Carfax form');
    setActiveForm('Carfax');
    return;
  }
  
  // CRITICAL: Extract Form.Type FIRST before any auto-triggering logic
  let formType = null;
  let formTitle = null;
  
  
  // Check for Form.Type in object format (new format)
  if (typeof parsedResponse['Form'] === 'object' && parsedResponse['Form']) {
    formType = parsedResponse['Form'].Type;
    formTitle = parsedResponse['Form'].Title;
    logger.log('🔍🔍🔍 FORM EXTRACTION - Extracted Type:', formType);
    logger.log('🔍🔍🔍 FORM EXTRACTION - Extracted Title:', formTitle);
    logger.log('🔥 handleFormLogic: Found Form object - Type:', formType, 'Title:', formTitle);
  } else {
    logger.log('🔍🔍🔍 FORM EXTRACTION - Form is not an object');
    logger.log('🔥 handleFormLogic: Form is not an object or is null/undefined');
  }
  
  // Check for Form as string (legacy format)
  if (!formType && typeof parsedResponse['Form'] === 'string') {
    formType = parsedResponse['Form'];
    logger.log('🔍🔍🔍 FORM EXTRACTION - Found Form as string:', formType);
    logger.log('🔥 handleFormLogic: Found Form as string:', formType);
  }
  
  // Check for FormTitle field (backward compatibility)
  if (!formTitle && parsedResponse['FormTitle']) {
    formTitle = parsedResponse['FormTitle'];
    logger.log('🔥 handleFormLogic: Found FormTitle field:', formTitle);
  }
  
  // Normalize plural form types to singular (Offers -> Offer, Contacts -> Contact, etc.)
  if (formType && formType.endsWith('s') && formType !== 'None' && formType !== 'Close') {
    const singularForm = formType.slice(0, -1);
    logger.log(`🔍🔍🔍 NORMALIZING - From "${formType}" to "${singularForm}"`);
    logger.log(`🔥 Normalizing plural form "${formType}" to singular "${singularForm}"`);
    formType = singularForm;
  }
  
  logger.log('🔍🔍🔍 FINAL FORM TYPE:', formType);
  logger.log('🔍🔍🔍 FINAL FORM TITLE:', formTitle);
  logger.log('🔥 handleFormLogic: Final Form.Type value:', formType);
  logger.log('🔥 handleFormLogic: Final Form.Title value:', formTitle);
  
  // ENHANCED: More comprehensive vehicle detection (only used if no explicit form)
  const hasVehicles = parsedResponse['Reply_Message']?.Content?.Inventory && 
                     Array.isArray(parsedResponse['Reply_Message'].Content.Inventory) && 
                     parsedResponse['Reply_Message'].Content.Inventory.length > 0;
  
  // Also check for vehicle-related keywords in the response text
  const responseText = typeof parsedResponse['Reply_Message'] === 'string' ? parsedResponse['Reply_Message'] : '';
  const hasVehicleKeywords = /\b(?:inventory|vehicles?|cars?|models?|available|certified|pre-owned|used|new)\b/i.test(responseText);
  const hasVehicleListingPattern = /\b(?:here\s+(?:are|is)|found|available)\b.*\b(?:vehicles?|cars?|models?|SUVs?)\b/i.test(responseText);
  
  const shouldTriggerContactForm = hasVehicles || hasVehicleKeywords || hasVehicleListingPattern;
  
  logger.log('🔥 handleFormLogic: hasVehicles check result:', hasVehicles);
  logger.log('🔥 handleFormLogic: hasVehicleKeywords result:', hasVehicleKeywords);
  logger.log('🔥 handleFormLogic: hasVehicleListingPattern result:', hasVehicleListingPattern);
  logger.log('🔥 handleFormLogic: shouldTriggerContactForm result:', shouldTriggerContactForm);
  
  // CRITICAL: Only auto-trigger Contact form if NO explicit form type is specified
  // This prevents overriding explicit Form.Type values like "Offer", "Offers", etc.
  if (shouldTriggerContactForm && (!formType || formType === 'None')) {
    logger.log('🔥 Vehicle-related content detected with no explicit form, auto-triggering Contact form');
    setActiveForm('Contact');
    return;
  }
  
  // CRITICAL: Strict check - only proceed if formType has a non-empty value
  if (!formType || formType.trim() === '') {
    logger.log('🔍🔍🔍 EARLY RETURN - formType is empty or null');
    logger.log('🔥 No Form.Type field in parsedResponse or empty string - ignoring');
    return;
  }

  logger.log('🔍🔍🔍 PROCEEDING WITH FORM LOGIC - formType:', formType);
  logger.log('🔥 Form.Type field value:', formType);

  // CRITICAL: Exact match only - do not collapse chat unless Form.Type is exactly "Close"
  if (formType === 'None') {
    logger.log('🔍🔍🔍 FORM TYPE IS "None" - Closing form');
    logger.log('Form is None, closing form if active');
    
    // Don't close Service or Appointment forms opened by classification
    if (activeForm === 'Service' || activeForm === 'Appointment') {
      logger.log('⏸️ Preserving', activeForm, 'form (likely opened by classification)');
      return;
    }
    
    if (activeForm && closeForm) {
      logger.log('Closing form');
      closeForm(false); // Don't force, respect user interaction
    }
  } else if (formType === 'Close') { // EXACT MATCH ONLY - case sensitive
    logger.log('🔍🔍🔍 FORM TYPE IS "Close" - Will minimize chat');
    logger.log('🔍🔍🔍 formType === "Close":', formType === 'Close');
    logger.log('🔍🔍🔍 typeof formType:', typeof formType);
    logger.log('🔍🔍🔍 formType length:', formType.length);
    logger.log('Form is Close, checking if should minimize chat window');
    
    // CRITICAL: Only close chat for messages from the current session
    // Don't close for restored messages from previous sessions
    if (messageTimestamp) {
      const sessionStart = getSessionStartTime();
      const msgTime = messageTimestamp instanceof Date ? messageTimestamp.getTime() : messageTimestamp;
      
      if (msgTime < sessionStart) {
        logger.log('🚫 Ignoring Close command - message is from previous session');
        logger.log('   Session started:', new Date(sessionStart).toISOString());
        logger.log('   Message timestamp:', new Date(msgTime).toISOString());
        return;
      }
      
      // Additional safeguard: Don't close if chat was just opened (within 5 seconds)
      const timeSinceSessionStart = Date.now() - sessionStart;
      if (timeSinceSessionStart < 5000) {
        logger.log('🚫 Ignoring Close command - chat was just opened', timeSinceSessionStart, 'ms ago');
        return;
      }
    }
    
    logger.log('✅ Close command from current session, minimizing chat window');
    
    // Close any active forms first
    if (activeForm && closeForm) {
      logger.log('Closing active form before minimizing chat');
      closeForm(true); // Force close when minimizing chat
    }
    
    // Minimize the chat window
    if (setIsExpanded) {
      logger.log('🔍🔍🔍 MINIMIZING CHAT WINDOW NOW!');
      logger.log('Minimizing chat window');
      setIsExpanded(false);
      
      // Dispatch event for host site to detect chat minimization
      window.dispatchEvent(new CustomEvent('d2c-chat-minimized', { detail: { expanded: false } }));
    } else {
      logger.log('🔍🔍🔍 setIsExpanded NOT AVAILABLE');
      logger.log('setIsExpanded function not available, cannot minimize chat');
    }
    
    return; // Exit early since we're closing the chat
  } else {
    logger.log('🔍🔍🔍 FORM TYPE IS NOT "None" OR "Close" - Activating form:', formType);
    // 🔥 NEW: Check if contact was recently submitted to prevent form reopening
    const contactSubmittedAt = localStorage.getItem('contactSubmittedAt');
    const contactSubmissionWindow = 10000; // 10 seconds
    const now = Date.now();
    
    if (contactSubmittedAt && (now - parseInt(contactSubmittedAt)) < contactSubmissionWindow) {
      logger.log('🚨 Contact was recently submitted, preventing form reopening for 10 seconds');
      logger.log('🚨🚨🚨 CONTACT RECENTLY SUBMITTED - BLOCKING FORM REOPENING');
      return;
    }
    
    // Check if a form was just closed (within 500ms) - if so, allow activation since this is a new form request
    // This is a very short window to prevent the same form from immediately reopening
    const lastFormCloseTime = localStorage.getItem('lastFormCloseTime');
    if (lastFormCloseTime) {
      const timeSinceClose = now - parseInt(lastFormCloseTime);
      logger.log('🚨 Time since last form close:', timeSinceClose, 'ms');
      // Only block if same form is trying to reopen within 500ms (prevents double-open bug)
      // Allow different form types to open immediately
    }
    
    logger.log('✅ Proceeding with form activation:', formType);
    
    logger.log('🔥 Triggering form:', formType);
    
    // Special handling for "Offer" form type - use setActiveForm to pass AI data including title
    // Note: "Offers" is now normalized to "Offer" earlier in the pipeline
    if (formType === 'Offer') {
      logger.log('Activating Offer form with AI data');

      // Get the complete Form object from AI response for passing title and other data
      let aiFormObject = typeof parsedResponse['Form'] === 'object' ? parsedResponse['Form'] : null;

      // If no object but we have formTitle, create an object
      if (!aiFormObject && formTitle) {
        aiFormObject = { Type: formType, Title: formTitle };
        logger.log('🔥 Created synthetic Form object with title:', aiFormObject);
      }

      // Block the Offer form when no vehicle is in context (home page, employees page, etc.)
      // The AI-provided Form object can carry a vehicle; otherwise fall back to page/stored interest.
      const aiVehicle = (aiFormObject as any)?.Vehicle || (aiFormObject as any)?.vehicle;
      if (!aiVehicle && !hasVehicleContext()) {
        logger.log('🚫 Offer form blocked: no vehicle in context');
        logger.warn('🚫 Offer form blocked: no vehicle in context');
        return;
      }

      setActiveForm('Offer', aiFormObject);
    } else {
      // Map FinanceV1 to Finance for form activation
      const mappedFormType = formType === 'FinanceV1' ? 'Finance' : formType;
      
      // Get the complete Form object from AI response for passing title and other data
      let aiFormObject = typeof parsedResponse['Form'] === 'object' ? parsedResponse['Form'] : null;
      
      // ENHANCED: If no object but we have formTitle, create an object
      if (!aiFormObject && formTitle) {
        aiFormObject = { Type: formType, Title: formTitle };
        logger.log('🔥 Created synthetic Form object with title:', aiFormObject);
      }
      
      // CRITICAL: Add Contact data to aiFormObject for pre-filling
      if (parsedResponse['Contact']) {
        aiFormObject = { ...aiFormObject, Contact: parsedResponse['Contact'] };
      }
      
      logger.log('🔥 Setting active form to:', mappedFormType);
      logger.log('🔥 AI Form object being passed:', JSON.stringify(aiFormObject, null, 2));
      logger.log('🚨🚨🚨 ABOUT TO CALL setActiveForm WITH:', mappedFormType, aiFormObject);
      logger.log('🔥 Before setActiveForm call');
      setActiveForm(mappedFormType, aiFormObject);
      logger.log('🚨🚨🚨 setActiveForm CALL COMPLETED!');
      logger.log('🔥 After setActiveForm call');
      
      
      // 🔥 NEW: Force expand form if it's FinanceV1
      if (formType === 'FinanceV1') {
        logger.log('🔥 FinanceV1 detected - will force expand form');
        // The expansion will be handled in the FormContext when formRef is available
      }
    }
  }
};
